/**
 * GNK 2026 본예산 수입편성 이관 부트스트랩
 * - 데이터: gnkIncomeRequests.data.js (gnk_bbud2.md)
 * - 마스터: (부서·사업·세항·목·세목·예산액) 그룹 — 첨부 조인행 ≠ 마스터 건수
 * - 디테일: 산출레벨~산출금액 (첨부 행 수와 동일)
 * - 수입 과목(세항>목>세목)·사업 카탈로그를 함께 시드 → 좌측 트리 표시
 * - 금액: 원 → 천원 수입 절사(floor)
 */
import {
  loadIncomeRequests,
  saveIncomeRequests,
  createRequestUid,
  createDetailId,
} from '../budgetIncomeRequestStore';
import {
  loadSubjectsByFilter,
  saveSubjects,
  BUDGET_TYPE_ALL,
  STRUCTURE_BUDGET_TYPE,
  STRUCTURE_PRESETS,
  validateStructure,
  getConfirmedStructure,
  loadStructurePayload,
  saveStructurePayload,
} from '../budgetStructureStore';
import {
  buildSubjectDispCode,
  buildSubjectDispName,
  ensureSubjectsIdentity,
} from '../budgetSubjectHistoryStore';
import { loadProjects, saveProjects, defaultProjectRow } from '../budgetProjectStore';
import { loadFunds } from '../budgetFundStore';
import { loadDepts } from '../../master/deptStore';
import { defaultAcctClassCode } from '../budgetAcctStore';
import { MAIN_KIND_CODE } from '../budgetRoundStore';
import { wonToThousand } from '../../utils/calcFormula';
import { ensureGnkProjectBootstrap, isGnkTenant } from './gnkProjects';
import { GNK_INCOME_REQUEST_ROWS } from './gnkIncomeRequests.data';

export { isGnkTenant };
export const GNK_INCOME_REQUEST_SOURCE = 'GNK 2026 본예산 수입이관(gnk_bbud2)';
export const GNK_INCOME_SUBJECT_SOURCE = 'GNK 2026 수입과목 이관(gnk_bbud2)';
export const GNK_INCOME_PROJECT_SOURCE = 'GNK 2026 수입사업 이관(gnk_bbud2)';
export const GNK_INCOME_FISCAL_YEAR = '2026';

/** 후원 계열 기본 재원 · 그 외 자체 */
const FUND_DONATION = '후원금1';
const FUND_OWN = '자체';

function norm(name) {
  return String(name || '').replace(/\s+/g, '').trim();
}

function normProjectName(name) {
  return norm(name).replace(/[,·]/g, '').replace(/\(예산\)$/, '');
}

/** GNK 수입: 레거시 세목 단위 — 지출 집행 프리셋(세목 최종)을 수입 구조로 확정 */
async function ensureIncomeStructure(dims, userId = 'GNK-INCOME-SEED') {
  const structureDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    inoutDiv: '수입',
    purpose: '집행',
  };
  const confirmed = getConfirmedStructure(structureDims);
  if (confirmed?.finalLevelName === '세목') return confirmed;

  const rows = JSON.parse(JSON.stringify(STRUCTURE_PRESETS.expense_exec));
  const v = validateStructure(rows);
  if (!v.canConfirm) return confirmed || null;

  const fy = String(structureDims.fiscalYear || new Date().getFullYear());
  const prev = await loadStructurePayload(structureDims);
  await saveStructurePayload(structureDims, {
    dims: structureDims,
    rows,
    status: '확정',
    purpose: structureDims.purpose,
    usedLevels: v.used,
    finalLevelName: v.finalLevelName,
    acctMapLevelNames: v.acctMapLevelNames,
    previewText: v.previewText,
    versionNo: (Number(prev?.versionNo) || 0) + 1 || 1,
    confirmedAt: new Date().toISOString(),
    confirmedBy: userId,
    startDate: `${fy}-01-01`,
    endDate: `${fy}-12-31`,
    remark: GNK_INCOME_SUBJECT_SOURCE,
    savedAt: new Date().toISOString(),
    savedBy: userId,
  });
  return getConfirmedStructure(structureDims);
}

/**
 * 첨부 세항>목>세목으로 수입 과목 카탈로그 시드 (트리·매핑용)
 */
async function ensureIncomeSubjects(dims) {
  await ensureIncomeStructure(dims);
  const subjectDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    purpose: '집행',
    budgetType: STRUCTURE_BUDGET_TYPE,
    inoutDiv: '수입',
  };
  const existing =
    (await loadSubjectsByFilter({ ...subjectDims, budgetType: BUDGET_TYPE_ALL })) || [];
  if (existing.some((r) => String(r.remark || '').includes(GNK_INCOME_SUBJECT_SOURCE))) {
    return existing;
  }

  const sehangMap = new Map(); // name -> code
  const mokMap = new Map(); // sehang|mok -> code
  const rows = [];
  let sort = 10;
  let sehangSeq = 0;

  const triples = [];
  const seen = new Set();
  GNK_INCOME_REQUEST_ROWS.forEach((src) => {
    const key = `${norm(src.sehangName)}|${norm(src.mokName)}|${norm(src.semokName)}`;
    if (seen.has(key)) return;
    seen.add(key);
    triples.push(src);
  });

  triples.forEach((src) => {
    const sehangName = src.sehangName;
    const mokName = src.mokName;
    const semokName = src.semokName;
    const sehangKey = norm(sehangName);
    if (!sehangMap.has(sehangKey)) {
      sehangSeq += 1;
      const code = `I${String(sehangSeq).padStart(2, '0')}`;
      sehangMap.set(sehangKey, code);
      rows.push({
        useYn: 'Y',
        fundUid: '',
        budgetType: STRUCTURE_BUDGET_TYPE,
        remark: GNK_INCOME_SUBJECT_SOURCE,
        levelName: '세항',
        parentCode: '',
        subjectCode: code,
        subjectName: sehangName,
        abbrName: sehangName,
        finalYn: 'N',
        sortOrder: sort,
      });
      sort += 10;
    }
    const sehangCode = sehangMap.get(sehangKey);
    const mokKey = `${sehangKey}|${norm(mokName)}`;
    if (!mokMap.has(mokKey)) {
      const mokIdx = [...mokMap.keys()].filter((k) => k.startsWith(`${sehangKey}|`)).length + 1;
      const code = `${sehangCode}${String(mokIdx).padStart(2, '0')}`;
      mokMap.set(mokKey, code);
      rows.push({
        useYn: 'Y',
        fundUid: '',
        budgetType: STRUCTURE_BUDGET_TYPE,
        remark: GNK_INCOME_SUBJECT_SOURCE,
        levelName: '목',
        parentCode: sehangCode,
        subjectCode: code,
        subjectName: mokName,
        abbrName: mokName,
        finalYn: 'N',
        sortOrder: sort,
      });
      sort += 10;
    }
    const mokCode = mokMap.get(mokKey);
    const semokIdx =
      rows.filter((r) => r.levelName === '세목' && r.parentCode === mokCode).length + 1;
    const semokCode = `${mokCode}${String(semokIdx).padStart(2, '0')}`;
    rows.push({
      useYn: 'Y',
      fundUid: '',
      budgetType: STRUCTURE_BUDGET_TYPE,
      remark: GNK_INCOME_SUBJECT_SOURCE,
      levelName: '세목',
      parentCode: mokCode,
      subjectCode: semokCode,
      subjectName: semokName,
      abbrName: semokName,
      finalYn: 'Y',
      sortOrder: sort,
    });
    sort += 10;
  });

  const seeded = ensureSubjectsIdentity(rows);
  await saveSubjects(subjectDims, seeded);
  return (await loadSubjectsByFilter({ ...subjectDims, budgetType: BUDGET_TYPE_ALL })) || seeded;
}

/**
 * 수입 사업명이 카탈로그에 없으면 세부사업으로 추가 (트리용 projectCode 확보)
 */
async function ensureIncomeProjects(dims) {
  await ensureGnkProjectBootstrap(dims, { userId: 'GNK-INCOME-SEED' });
  const projectDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    purpose: '집행',
    budgetType: STRUCTURE_BUDGET_TYPE,
  };
  let projects = (await loadProjects(projectDims)) || [];
  const nameMap = buildProjectNameMap(projects);
  const usedCodes = new Set(projects.map((p) => String(p.projectCode || '')));
  let nextNo = 900001;
  const added = [];

  const bizNames = [...new Set(GNK_INCOME_REQUEST_ROWS.map((r) => r.bizName).filter(Boolean))];
  bizNames.forEach((bizName) => {
    if (resolveProject(bizName, nameMap)) return;
    while (usedCodes.has(String(nextNo))) nextNo += 1;
    const code = String(nextNo);
    usedCodes.add(code);
    nextNo += 1;
    const row = defaultProjectRow({
      levelName: '세부',
      budgetType: STRUCTURE_BUDGET_TYPE,
      parentCode: '',
      projectCode: code,
      projectName: bizName,
      finalYn: 'Y',
      useYn: 'Y',
      sortOrder: (projects.length + added.length + 1) * 10,
      remark: GNK_INCOME_PROJECT_SOURCE,
      startYear: GNK_INCOME_FISCAL_YEAR,
      endYear: '2100',
    });
    added.push(row);
    nameMap.set(normProjectName(bizName), row);
  });

  if (added.length) {
    projects = [...projects, ...added];
    await saveProjects(projectDims, projects);
  }
  return projects;
}

function buildSubjectChainMaps(subjects) {
  const byCode = new Map();
  subjects.forEach((r) => {
    byCode.set(`${r.budgetType || ''}::${r.subjectCode}`, r);
    if (!byCode.has(`::${r.subjectCode}`)) byCode.set(`::${r.subjectCode}`, r);
  });

  const chainOf = (row) => {
    const chain = { 세항: '', 목: '', 세목: '' };
    let cur = row;
    let guard = 0;
    while (cur && guard++ < 12) {
      if (cur.levelName === '세항' || cur.levelName === '목' || cur.levelName === '세목') {
        chain[cur.levelName] = norm(cur.subjectName);
      }
      if (!cur.parentCode) break;
      cur =
        byCode.get(`${cur.budgetType || ''}::${cur.parentCode}`) ||
        byCode.get(`::${cur.parentCode}`) ||
        null;
    }
    return chain;
  };

  const fullMap = new Map();
  const mokMap = new Map();
  const semokMap = new Map();
  subjects
    .filter((r) => r.finalYn === 'Y' && r.useYn !== 'N' && r.mod !== 'D')
    .forEach((r) => {
      const chain = chainOf(r);
      if (!chain.세목) return;
      const fullKey = `${chain.세항}|${chain.목}|${chain.세목}`;
      if (!fullMap.has(fullKey)) fullMap.set(fullKey, r);
      const mokKey = `${chain.목}|${chain.세목}`;
      if (!mokMap.has(mokKey)) mokMap.set(mokKey, r);
      if (!semokMap.has(chain.세목)) semokMap.set(chain.세목, []);
      semokMap.get(chain.세목).push(r);
    });
  return { fullMap, mokMap, semokMap };
}

function buildProjectNameMap(projects) {
  const priority = { 세부: 0, 단위: 1, 정책: 2 };
  const map = new Map();
  (projects || [])
    .filter((r) => r.useYn !== 'N')
    .forEach((r) => {
      const key = normProjectName(r.projectName);
      if (!key) return;
      const prev = map.get(key);
      if (!prev || (priority[r.levelName] ?? 9) < (priority[prev.levelName] ?? 9)) {
        map.set(key, r);
      }
    });
  return map;
}

function resolveProject(bizName, projectMap) {
  const key = normProjectName(bizName);
  if (!key) return null;
  if (projectMap.has(key)) return projectMap.get(key);
  const stripped = key.replace(/\([^()]*\)$/, '');
  if (stripped && stripped !== key && projectMap.has(stripped)) {
    return projectMap.get(stripped);
  }
  let hit = null;
  let hitLen = 0;
  for (const [name, row] of projectMap.entries()) {
    if (name.includes(key) || key.includes(name)) {
      if (name.length > hitLen) {
        hit = row;
        hitLen = name.length;
      }
    }
  }
  return hit;
}

function pickDefaultFund(funds, sehangName) {
  const list = (funds || []).filter((f) => f.useYn !== 'N');
  const prefer = String(sehangName || '').includes('후원') ? FUND_DONATION : FUND_OWN;
  return (
    list.find((f) => norm(f.fundName) === norm(prefer)) ||
    list.find((f) => norm(f.fundName) === norm(FUND_OWN)) ||
    list[0] ||
    null
  );
}

function needsIncomeRequestReseed(existing) {
  if (!existing.length) return true;
  const pristineSeed = existing.every((r) =>
    String(r.remark || '').includes(GNK_INCOME_REQUEST_SOURCE)
  );
  if (!pristineSeed) return false;
  // 과목·사업 코드 없으면 트리 불가 → 재적재
  return existing.some((r) => !String(r.subjectCode || '').trim() || !String(r.projectCode || '').trim());
}

/**
 * GNK · 2026 · 본예산(100) 수입편성요구 시드
 * - 비어 있거나, 이관분이지만 과목/사업 코드가 비어 있으면 재적재
 */
export async function ensureGnkIncomeRequestBootstrap(dims) {
  const result = {
    ok: true,
    seeded: false,
    count: 0,
    detailCount: 0,
    unmatchedSubjects: [],
    unmatchedProjects: 0,
  };
  if (!isGnkTenant(dims?.tenantCode)) return result;
  if (String(dims?.fiscalYear || '') !== GNK_INCOME_FISCAL_YEAR) return result;
  if (String(dims?.budgetKindCode || '') !== MAIN_KIND_CODE) return result;

  const baseDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    budgetKindCode: MAIN_KIND_CODE,
  };
  const existing = await loadIncomeRequests(baseDims);
  if (!needsIncomeRequestReseed(existing)) {
    result.count = existing.length;
    result.detailCount = existing.reduce((s, r) => s + (r.details || []).length, 0);
    return result;
  }

  const catalogDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    purpose: '집행',
    budgetType: BUDGET_TYPE_ALL,
    inoutDiv: '수입',
  };

  const subjects = await ensureIncomeSubjects(dims);
  const projects = await ensureIncomeProjects(dims);
  const { fullMap, mokMap, semokMap } = buildSubjectChainMaps(subjects);
  const projectMap = buildProjectNameMap(projects);
  const funds = (await loadFunds(catalogDims)) || [];
  const depts = (await loadDepts(dims.tenantCode)) || [];
  const deptByCode = new Map(depts.map((d) => [String(d.deptCode).toUpperCase(), d]));
  const unmatchedSubjectSet = new Set();
  let detailCount = 0;

  const masters = GNK_INCOME_REQUEST_ROWS.map((src) => {
    const dept = deptByCode.get(String(src.deptCode).toUpperCase()) || null;

    const fullKey = `${norm(src.sehangName)}|${norm(src.mokName)}|${norm(src.semokName)}`;
    const mokKey = `${norm(src.mokName)}|${norm(src.semokName)}`;
    let subject = fullMap.get(fullKey) || mokMap.get(mokKey) || null;
    if (!subject) {
      const candidates = semokMap.get(norm(src.semokName)) || [];
      if (candidates.length === 1) subject = candidates[0];
    }
    if (!subject) {
      unmatchedSubjectSet.add(`${src.sehangName}>${src.mokName}>${src.semokName}`);
    }

    const project = resolveProject(src.bizName, projectMap);
    if (!project) result.unmatchedProjects += 1;

    const defaultFund = pickDefaultFund(funds, src.sehangName);
    const requestUid = createRequestUid();
    const remarks = [GNK_INCOME_REQUEST_SOURCE];
    if (!subject) remarks.push(`과목 미매핑: ${src.sehangName}>${src.mokName}>${src.semokName}`);
    if (!project) remarks.push(`사업 미지정(레거시: ${src.bizName})`);

    const details = (src.details || []).map((d) => {
      const detailId = createDetailId();
      const won = Number(d.calcAmtWon) || 0;
      detailCount += 1;
      return {
        detailId,
        id: detailId,
        calcLevel: Number(d.calcLevel) || 1,
        calcDesc: d.calcDesc || '',
        calcBasis: d.calcBasis || '',
        calcAmt: wonToThousand(won, '수입'),
        remark: d.remark || '',
        basisLocked: false,
        mod: '',
      };
    });

    return {
      requestUid,
      id: requestUid,
      deptCode: dept?.deptCode || src.deptCode,
      deptName: dept?.deptName || src.deptName,
      projectCode: project?.projectCode || '',
      projectName: project?.projectName || src.bizName || '',
      budgetType: subject?.budgetType || STRUCTURE_BUDGET_TYPE,
      subjectUid: subject?.subjectUid || '',
      subjectCode: subject?.subjectCode || '',
      subjectDispCode: subject ? buildSubjectDispCode(subject, subjects) : '',
      subjectName: subject
        ? buildSubjectDispName(subject, subjects)
        : `${src.sehangName} - ${src.semokName}`,
      fundUid: defaultFund?.fundUid || subject?.fundUid || '',
      fundCode: defaultFund?.fundCode || '',
      fundName: defaultFund?.fundName || '',
      fundPkgUid: '',
      fundAmounts: defaultFund
        ? [{ fundUid: defaultFund.fundUid, ratioBp: 10000, amount: null }]
        : [],
      remark: remarks.join(' · '),
      details,
      mod: '',
    };
  });

  saveIncomeRequests(
    {
      ...baseDims,
      acctClass: String(dims.acctClass || '') || defaultAcctClassCode(dims.tenantCode),
    },
    masters
  );
  result.seeded = true;
  result.count = masters.length;
  result.detailCount = detailCount;
  result.unmatchedSubjects = [...unmatchedSubjectSet];
  return result;
}
